import React, { useState, useEffect, useContext, useRef } from "react";
import { useForm } from "react-hook-form";
import { useSnackbar } from "notistack";
import {
  Avatar,
  TextField,
  Button,
  Box,
  IconButton,
  InputLabel,
  FormControl,
  CircularProgress,
  Typography,
} from "@mui/material";
import { MuiTelInput } from "mui-tel-input";
import { ThemeContext } from "../../context/ThemeContext";
import axios from "axios";
import { jwtDecode } from "../../utils/auth";
import { useAuthContext } from "../../hooks/contexts/useAuthContext";
import { useNavigate } from "react-router-dom";
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import defaultLogo from "../../assets/logo/default.jpg";
import { getColors } from "../../themes/theme";

const ProfilePage = () => {
  const { fetchMe, setAvatar, token } = useAuthContext();
  const navigate = useNavigate();
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [selectedLogo, setSelectedLogo] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const { darkMode } = useContext(ThemeContext);
  const { enqueueSnackbar } = useSnackbar();
  const userID = jwtDecode(token)?.userId;
  const logoInputRef = useRef(null);
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({});

  useEffect(() => {
    if (token) {
      axios
        .post(
          `${process.env.REACT_APP_API_URL}/profile/get`,
          {
            userid: userID,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
        .then((res) => {
          setValue("practiceName", res.data.practiceName);
          setValue("practiceEmail", res.data.practiceEmail);
          setValue("reportDescription", res.data.reportLinkDescription);
          setValue("phoneNumber", res.data.phoneNumber);
          setValue("address1", res.data.addressFirstLine);
          setValue("address2", res.data.addressSecondLine);
          setValue("city", res.data.city);
          setValue("state", res.data.state);
          setValue("country", res.data.country);
          setValue("postalCode", res.data.postalCode);
          setValue("reportUrl", res.data.reportLinkURL);
          setValue("postureco_key", res.data.postureco_key);
          setValue("avatar", res.data.avatar);
          if (res.data.logo !== "default.jpg") setValue("logo", res.data.logo);
          else setSelectedLogo(defaultLogo);
        });
    }
  }, [setValue, userID, token]);

  const handleAvatarChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setAvatarFile(file);
      const imageUrl = URL.createObjectURL(file);
      setSelectedAvatar(imageUrl);
      setAvatar(imageUrl);
    }
  };

  const handleLogoChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate MIME type
    if (!file.type || !file.type.startsWith("image/")) {
      alert("Please select a valid image file (e.g., JPG, PNG, GIF).");
      event.target.value = ""; // reset file input
      setLogoFile?.(null);     // optional: clear state
      // setSelectedLogo?.(defaultLogo); // optional: reset preview if you keep a default
      return;
    }

    setLogoFile(file);
    const imageUrl = URL.createObjectURL(file);
    setSelectedLogo(imageUrl);
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      const formData = new FormData();
      if (avatarFile) {
        formData.append("avatar", avatarFile);
      }
      if (logoFile) {
        formData.append("logo", logoFile);
      }
      formData.append("userid", userID);
      for (const key in data) {
        if (data[key]) {
          formData.append(key, data[key]);
        }
      }

      await axios.post(
        `${process.env.REACT_APP_API_URL}/profile/edit`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      await fetchMe();
      enqueueSnackbar("Profile updated successfully", { variant: "success" });
      setLoading(false);
      navigate(-1);
    } catch (error) {
      console.error("Error updating profile:", error);
      enqueueSnackbar("Error updating profile", { variant: "error" });
    }
  };

  return (
    <>
      {loading ? (
        <Box
          display={"flex"}
          justifyContent="center"
          alignItems="center"
          height="100vh"
        >
          <CircularProgress size={50} />
        </Box>
      ) : (
        <Box sx={{ padding: { xs: 2, sm: 5 } }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "50px",
                mb: 6,
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  width: { xs: 200, sm: 200, md: 234 },
                  height: { xs: 200, sm: 200, md: 234 },
                  overflow: "hidden",
                  cursor: "pointer",
                }}
              >
                <Avatar
                  alt="avatar"
                  src={
                    selectedAvatar ||
                    (watch("avatar") ? `${watch("avatar")}` : undefined)
                  }
                  sx={{ width: "100%", height: "100%" }}
                />

                <input
                  accept="image/*"
                  type="file"
                  id="avatar-upload"
                  style={{ display: "none" }}
                  onChange={handleAvatarChange}
                />
                <label htmlFor="avatar-upload">
                  <IconButton
                    component="span"
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      borderRadius: "50%",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 8, // Adjust this value to fine-tune the position
                      right: 12, // Adjust this value to fine-tune the position
                      backgroundColor: "rgba(0, 0, 0, 0.3)",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "opacity 0.3s ease-in-out",
                    }}
                  >
                    <Box
                      display={"flex"}
                      justifyContent={"flex-end"}
                      width="max-content"
                      sx={{
                        borderRadius: "50%",
                        padding: "8px",
                        color: darkMode ? "black" : "#007FFF",
                        bgcolor: darkMode ? "#999999" : "#ffffff",
                        alignItems: "center",
                        "&:hover": {
                          color: darkMode ? "white" : "blue",
                        },
                        cursor: "pointer",
                      }}
                    >
                      <BorderColorOutlinedIcon />
                    </Box>
                  </Box>
                </label>
              </Box>

              <Box sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  label="Practice Name"
                  variant="filled"
                  placeholder="Enter your Practice Name"
                  sx={{ mb: 4 }}
                  {...register("practiceName", {
                    required: "Practice name is required",
                  })}
                  error={!!errors.practiceName}
                  helperText={errors.practiceName?.message}
                />
                <TextField
                  fullWidth
                  label="Practice Email"
                  variant="filled"
                  placeholder="Enter your Practice Email"
                  {...register("practiceEmail", {
                    required: "Email is required",
                    pattern: {
                      value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                      message: "Invalid email address",
                    },
                  })}
                  error={!!errors.practiceEmail}
                  helperText={errors.practiceEmail?.message}
                />
              </Box>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <TextField
                fullWidth
                label="Report Link Description"
                variant="filled"
                multiline
                rows={4}
                placeholder="Enter your Report Description"
                {...register("reportDescription")}
              />
              <FormControl
                {...register("phoneNumber", {
                  required: "Phone number is required",
                })}
              >
                <InputLabel htmlFor="phonenumber">PhoneNumber</InputLabel>
                <MuiTelInput
                  id="phonenumber"
                  fullWidth
                  defaultCountry="US"
                  value={watch("phoneNumber")}
                  sx={{
                    "& .MuiTelInput-IconButton": {
                      mr: "12px",
                      borderRadius: "5px",
                      width: "80px",
                      height: "60px",
                      background: darkMode ? "#313235" : "#F0F0F0",
                      "& .MuiTelInput-FlagImg": {
                        width: "33px",
                        height: "25px",
                      },
                      "&:hover": {
                        background: darkMode ? "#3A3B3E" : "#EBEBEB",
                      },
                    },
                  }}
                  onChange={(value) => setValue("phoneNumber", value)}
                  error={!!errors.phoneNumber}
                  helperText={errors.phoneNumber?.message}
                  placeholder="Enter your Phone number"
                />
              </FormControl>
              <TextField
                fullWidth
                label="Address Line 1"
                variant="filled"
                {...register("address1")}
                placeholder="Enter your Address"
              />
              <TextField
                fullWidth
                label="Address Line 2 (Optional)"
                variant="filled"
                {...register("address2")}
                placeholder="Enter your Address"
              />
              <Box sx={{ display: "flex", gap: 4 }}>
                <TextField
                  fullWidth
                  label="City"
                  variant="filled"
                  {...register("city")}
                  placeholder="Enter your City"
                />
                <TextField
                  fullWidth
                  label="State"
                  variant="filled"
                  {...register("state")}
                  placeholder="Enter your State"
                />
              </Box>
              <Box sx={{ display: "flex", gap: 4 }}>
                <TextField
                  fullWidth
                  label="Country"
                  variant="filled"
                  {...register("country")}
                  placeholder="Enter your Country"
                />
                <TextField
                  fullWidth
                  label="Postal Code"
                  variant="filled"
                  {...register("postalCode")}
                  placeholder="Enter your Postal Code"
                />
              </Box>
              <TextField
                fullWidth
                label="Report Link URL"
                variant="filled"
                {...register("reportUrl")}
                placeholder="Enter your Report Link URL"
              />
            </Box>

            <Box mt={6}>
              <Box>
                <InputLabel>Logo</InputLabel>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1, whiteSpace: "pre-line" }}
                >
                  Please upload image files (JPG and PNG only). 400 px × 400 px recommended.
                  {"\n"}
                  Logo will be used in your reports.
                </Typography>
                <Box
                  display="flex"
                  alignItems="center"
                  gap={2}
                  flexDirection={{ xs: "column", sm: "row" }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      width: { xs: 180, sm: 200, md: 234 },
                      height: { xs: 180, sm: 200, md: 234 },
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                      cursor: "pointer",
                      borderWidth: "1px",
                      borderStyle: "solid",
                      borderColor: darkMode
                        ? getColors.listItemTextLight
                        : getColors.listItemTextDark,
                    }}
                  >
                    <Avatar
                      alt="logo"
                      variant="square"
                      src={selectedLogo || `${watch("logo")}`}
                      sx={{
                        width: "100%",
                        height: "auto",
                        objectFit: "contain",
                        bgcolor: "transparent",
                      }}
                    />

                    <input
                      accept="image/*"
                      type="file"
                      id="logo-upload"
                      style={{ display: "none" }}
                      onChange={handleLogoChange}
                      ref={logoInputRef}
                    />
                    <label htmlFor="logo-upload">
                      <IconButton
                        component="span"
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          borderRadius: 0,
                        }}
                      />
                    </label>
                  </Box>
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Button
                      variant="contained"
                      sx={{
                        px: "27px",
                        py: "12px",
                        fontSize: "18px",
                        borderRadius: "10px",
                      }}
                      onClick={() => logoInputRef.current?.click()}
                    >
                      Upload
                    </Button>
                    <Button
                      variant="contained"
                      sx={{
                        px: "27px",
                        py: "12px",
                        fontSize: "18px",
                        borderRadius: "10px",
                      }}
                      onClick={() => {
                        setSelectedLogo(defaultLogo);
                        setLogoFile(null);
                      }}
                    >
                      Reset
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Box>


            <Box sx={{ mt: 5, display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                sx={{
                  px: "27px",
                  py: "12px",
                  fontSize: "18px",
                  borderRadius: "10px",
                }}
                type="submit"
              >
                Save Changes
              </Button>
            </Box>
          </form>
        </Box>
      )}
    </>
  );
};

export default ProfilePage;
